// Visitor accessibility preferences: stored in localStorage, applied as data-*
// attributes on <html> and styled in index.css. src/a11y/bootstrap.js applies
// the same attributes before first paint — keep the two in sync (a test checks).

export const STORAGE_KEY = 'gia:a11y'

export const TEXT_SIZES = [100, 115, 130, 150] as const
export type TextSize = (typeof TEXT_SIZES)[number]

export interface A11yPreferences {
  textSize: TextSize
  highContrast: boolean
  readableFont: boolean
  textSpacing: boolean
  highlightLinks: boolean
  reduceMotion: boolean
  readingGuide: boolean
}

export const DEFAULT_PREFERENCES: A11yPreferences = {
  textSize: 100,
  highContrast: false,
  readableFont: false,
  textSpacing: false,
  highlightLinks: false,
  reduceMotion: false,
  readingGuide: false,
}

export type ToggleKey = Exclude<keyof A11yPreferences, 'textSize'>

/** data-* attribute (on <html>) each toggle sets to "true" when enabled. */
export const TOGGLE_ATTRIBUTES: Record<ToggleKey, string> = {
  highContrast: 'data-a11y-contrast',
  readableFont: 'data-a11y-font',
  textSpacing: 'data-a11y-spacing',
  highlightLinks: 'data-a11y-links',
  reduceMotion: 'data-a11y-motion',
  readingGuide: 'data-a11y-guide',
}
export const TEXT_SIZE_ATTRIBUTE = 'data-a11y-text'

/** Parse stored JSON defensively: unknown keys dropped, bad values defaulted. */
export function parsePreferences(raw: string | null | undefined): A11yPreferences {
  if (!raw) return { ...DEFAULT_PREFERENCES }
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return { ...DEFAULT_PREFERENCES }
  }
  if (!data || typeof data !== 'object') return { ...DEFAULT_PREFERENCES }
  const input = data as Record<string, unknown>
  const prefs: A11yPreferences = { ...DEFAULT_PREFERENCES }
  if (TEXT_SIZES.includes(input.textSize as TextSize)) prefs.textSize = input.textSize as TextSize
  for (const key of Object.keys(TOGGLE_ATTRIBUTES) as ToggleKey[]) {
    if (typeof input[key] === 'boolean') prefs[key] = input[key] as boolean
  }
  return prefs
}

/** Only non-default values are stored, so defaults can evolve later. */
export function serializePreferences(prefs: A11yPreferences): string {
  const diff: Partial<A11yPreferences> = {}
  for (const key of Object.keys(DEFAULT_PREFERENCES) as (keyof A11yPreferences)[]) {
    if (prefs[key] !== DEFAULT_PREFERENCES[key]) {
      ;(diff as Record<string, unknown>)[key] = prefs[key]
    }
  }
  return JSON.stringify(diff)
}

export function isDefault(prefs: A11yPreferences): boolean {
  return serializePreferences(prefs) === '{}'
}

/** Attributes to set on <html> for these preferences (absent = not set). */
export function preferenceAttributes(prefs: A11yPreferences): Record<string, string> {
  const attrs: Record<string, string> = {}
  if (prefs.textSize !== 100) attrs[TEXT_SIZE_ATTRIBUTE] = String(prefs.textSize)
  for (const [key, attr] of Object.entries(TOGGLE_ATTRIBUTES) as [ToggleKey, string][]) {
    if (prefs[key]) attrs[attr] = 'true'
  }
  return attrs
}

export function applyPreferences(prefs: A11yPreferences, root: HTMLElement = document.documentElement): void {
  const attrs = preferenceAttributes(prefs)
  for (const attr of [TEXT_SIZE_ATTRIBUTE, ...Object.values(TOGGLE_ATTRIBUTES)]) {
    if (attr in attrs) root.setAttribute(attr, attrs[attr])
    else root.removeAttribute(attr)
  }
}

export function loadPreferences(): A11yPreferences {
  try {
    return parsePreferences(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    return { ...DEFAULT_PREFERENCES } // storage blocked (private mode, etc.)
  }
}

export function savePreferences(prefs: A11yPreferences): void {
  try {
    if (isDefault(prefs)) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, serializePreferences(prefs))
  } catch {
    // Storage unavailable: preferences still apply for this visit.
  }
}

/** True when motion should be reduced: OS setting or the visitor's choice. */
export function motionReduced(): boolean {
  if (typeof window === 'undefined') return false
  return (
    document.documentElement.getAttribute(TOGGLE_ATTRIBUTES.reduceMotion) === 'true' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}
